import type { ProgressQueueRow, ProgressQueueView, ProgressToWriteRow } from '@ehr/shared';
import { noteCodeLines, normalizeTime } from '@ehr/shared';
import { clockTime, storedTime, timeOpts } from './timePrefs.js';
import { noteDate } from './noteDate.js';

/**
 * Progress Notes page (ehr#181, Chase's PROGRESS_NOTES_09-29-V1, V3 of 10-04): count tiles
 * pick one list, shown below as a single table with a client search and a filter bar.
 * reminders#2 adds To write, first: sessions checked out in the Calendar with no note yet.
 * Pure helpers, no DOM.
 */

export type QueueListId =
  'toWrite' | 'toReview' | 'returned' | 'drafts' | 'withReviewer' | 'history';
/** 'write' opens the session's note: the one it has, or a new one filled from the session. */
export type QueueMode = 'write' | 'review' | 'edit' | 'view';

export interface QueueList {
  id: QueueListId;
  label: string;
  /** Tiles that need the user to act get an "Action needed" tag while they hold notes. */
  needsAction: boolean;
  desc: string;
  emptyTitle: string;
  emptyBody: string;
  /** The row button, and how the note opens. */
  action: string;
  primary: boolean;
  mode: QueueMode;
}

export function queueLists(manager: boolean): QueueList[] {
  return [
    {
      id: 'toWrite',
      label: 'To write',
      needsAction: true,
      desc: 'Your sessions checked out in the last 60 days with no note yet.',
      emptyTitle: 'No notes to write',
      emptyBody:
        'When you check out a session in the Calendar, it waits here until you start its note.',
      action: 'Write note',
      primary: true,
      mode: 'write',
    },
    {
      id: 'toReview',
      label: 'To review',
      needsAction: true,
      desc: manager
        ? 'Notes waiting for sign-off in the practice. Co-sign them or return them with changes.'
        : 'Notes sent to you to co-sign or return with changes.',
      emptyTitle: 'Nothing waiting for your review',
      emptyBody: manager
        ? 'Notes submitted for sign-off in the practice show up here.'
        : 'Notes your supervisees submit show up here.',
      action: 'Review',
      primary: true,
      mode: 'review',
    },
    {
      id: 'returned',
      label: 'Returned to me',
      needsAction: true,
      desc: 'Your reviewer asked for changes. Edit, reply and resubmit.',
      emptyTitle: 'No notes returned to you',
      emptyBody: 'If a reviewer asks for changes, the note comes back here.',
      action: 'Edit and resubmit',
      primary: true,
      mode: 'edit',
    },
    {
      id: 'drafts',
      label: 'My drafts',
      needsAction: false,
      desc: 'Started but not yet submitted for review.',
      emptyTitle: 'No drafts',
      emptyBody: 'Start a note from a session, or with + New note.',
      action: 'Continue',
      primary: false,
      mode: 'edit',
    },
    {
      id: 'withReviewer',
      label: 'With reviewer',
      needsAction: false,
      // ehr#854: + notes sent back with changes, until their author resubmits them
      desc: manager
        ? 'Submitted and locked until your reviewer co-signs or returns them. Notes sent back with changes in the practice also wait here until the clinician resubmits them.'
        : 'Submitted and locked until your reviewer co-signs or returns them. Notes you sent back with changes also wait here until the clinician resubmits them.',
      emptyTitle: 'Nothing waiting with a reviewer',
      emptyBody:
        'Notes you submit for review wait here until they are co-signed. Notes you send back with changes wait here until they are resubmitted.',
      action: 'View',
      primary: false,
      mode: 'view',
    },
    {
      id: 'history',
      label: 'History',
      needsAction: false,
      desc: 'Signed and co-signed notes you wrote or reviewed.',
      emptyTitle: 'No signed notes yet',
      emptyBody: 'Once a note is signed or co-signed it is kept here.',
      action: 'View',
      primary: false,
      mode: 'view',
    },
  ];
}

/** The list to open on: the first one needing action that has notes, else the first with
 *  any notes, else To review. */
export function defaultList(q: ProgressQueueView, lists: QueueList[]): QueueListId {
  const act = lists.find((l) => l.needsAction && (q[l.id] ?? []).length > 0);
  if (act) return act.id;
  const any = lists.find((l) => (q[l.id] ?? []).length > 0);
  return any ? any.id : 'toReview';
}

/** Client-name search: every word must appear in the name, any order and case. */
export function filterByClient(rows: ProgressQueueRow[], text: string): ProgressQueueRow[] {
  const words = text.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return rows;
  return rows.filter((r) => {
    const name = (r.contactName ?? '').toLowerCase();
    return words.every((w) => name.includes(w));
  });
}

const ms = (v: string | Date | null | undefined): number => {
  const t = v ? new Date(v).getTime() : NaN;
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

/**
 * reminders#2 - one session of a calendar event, in local time: the event's own start
 * time of day (as this browser shows it) on the session's day. A recurring series keeps
 * its wall-clock time across daylight-saving changes, as the Calendar shows it.
 */
export function sessionStart(seriesStartsAt: string | Date, dateKey: string): Date {
  const s = new Date(seriesStartsAt);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  if (!m || Number.isNaN(s.getTime())) return s;
  return new Date(+m[1]!, +m[2]! - 1, +m[3]!, s.getHours(), s.getMinutes());
}

/** The queue as the page shows it: To write rows dated on their session, in local time. */
export function localizeQueue(q: ProgressQueueView): ProgressQueueView {
  return {
    ...q,
    toWrite: (q.toWrite ?? []).map((r) => ({
      ...r,
      date: sessionStart(r.seriesStartsAt, r.dateKey),
    })),
  };
}

/** The Progress Notes nav badge: notes to write, to review, and returned to me. */
export const badgeCount = (q: ProgressQueueView): number =>
  (q.toWrite?.length ?? 0) + q.toReview.length + q.returned.length;

/** "9:00 AM" (or "09:00", ehr#98) for a session start (the Clinical Notes appointment link's time). */
export function timeLabel(d: Date): string {
  return clockTime(d);
}

/**
 * A row's "Sat, Sep 12, 3:00 PM": the session's day, then the note's own Start when it has one.
 * ehr#790: the list read the time off the note's date (the appointment's instant, or a seeded
 * one), so it could differ from the Start inside the note (8:00 AM in the list, 3:00 PM in the
 * note). A note with no Start shows its date's time, or only the day for a picked date.
 */
export function sessionWhen(r: Pick<ProgressQueueRow, 'date' | 'sessionTime'>): string {
  const d = noteDate(r.date);
  const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const start = normalizeTime(r.sessionTime ?? '');
  if (start) return `${day}, ${storedTime(start)}`;
  if (!d.getHours() && !d.getMinutes()) return day;
  return `${day}, ${d.toLocaleTimeString('en-US', timeOpts())}`;
}

/**
 * The Clinical Notes appointment link for a To write row (calendar#46 step 6): the note it
 * already has, or a new one filled from the session. Call on a localized row.
 */
export function writeLinkParams(r: ProgressToWriteRow): Record<string, string> {
  const out: Record<string, string> = {
    appt: r.appointmentKey,
    appt_time: timeLabel(new Date(r.date)),
    appt_type: r.sessionLabel,
  };
  if (r.durationMin) out.appt_dur = String(r.durationMin);
  return out;
}

/** The moment each list waits from: sent for review, returned, or the session itself. */
export function waitingSince(row: ProgressQueueRow, list: QueueListId): string | Date {
  if (list === 'toReview' || list === 'withReviewer') return row.submittedAt ?? row.updatedAt;
  if (list === 'returned') return row.updatedAt;
  return row.date;
}

/** Oldest first, so the note waiting longest is on top. */
export function sortOldest(rows: ProgressQueueRow[], list: QueueListId): ProgressQueueRow[] {
  return rows.slice().sort((a, b) => ms(waitingSince(a, list)) - ms(waitingSince(b, list)));
}

/** Whole calendar days from `from` to `now`, in local time (0 = today; never negative). */
export function daysSince(from: string | Date, now: Date = new Date()): number {
  const a = new Date(from);
  if (Number.isNaN(a.getTime())) return 0;
  const start = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.max(0, Math.round((end - start) / 86400000));
}

/** Waiting this many days or more is flagged. */
export const WARN_DAYS = 3;

export const waitingText = (days: number): string =>
  days === 0 ? 'Today' : days === 1 ? '1 day' : `${days} days`;

/** History rows: newest signature first. */
export function sortNewest(rows: ProgressQueueRow[]): ProgressQueueRow[] {
  const at = (r: ProgressQueueRow) => new Date(r.signedAt ?? r.updatedAt).getTime() || 0;
  return rows.slice().sort((a, b) => at(b) - at(a));
}

// ---------- Filter bar (ehr#181 V2 / V3) ----------

export const STATUS_LABELS: Record<string, string> = { signed: 'Signed', cosigned: 'Co-signed' };

export const PERIODS = [
  { id: '7', label: 'Last 7 days', days: 7 },
  { id: '30', label: 'Last 30 days', days: 30 },
  { id: '90', label: 'Last 90 days', days: 90 },
  { id: '365', label: 'Last year', days: 365 },
  { id: 'custom', label: 'Custom range', days: 0 },
] as const;

export type MultiKey = 'authorId' | 'reviewerId' | 'template' | 'cpt' | 'status';
export const MULTI_KEYS: MultiKey[] = ['authorId', 'reviewerId', 'template', 'cpt', 'status'];

export interface NoteFilters {
  authorId: string[];
  reviewerId: string[];
  /** Note format (SOAP / DAP / BIRP / CREDIFY). */
  template: string[];
  cpt: string[];
  /** History only: signed / cosigned. */
  status: string[];
  /** '' = any time, a PERIODS id, or 'custom' (from / to). */
  period: string;
  /** ISO YYYY-MM-DD, custom range only. */
  from: string;
  to: string;
}

export const emptyFilters = (): NoteFilters => ({
  authorId: [],
  reviewerId: [],
  template: [],
  cpt: [],
  status: [],
  period: '',
  from: '',
  to: '',
});

/** A row's values for one field. ehr#528: a note billed "90839 + 90840" (or "H0038 x2")
 *  has one CPT value per code, without the units. */
const valuesOf = (r: ProgressQueueRow, key: MultiKey): string[] => {
  if (key === 'cpt') {
    const codes = noteCodeLines(r.cpt).map((l) => l.code);
    return codes.length ? codes : r.cpt ? [r.cpt] : [];
  }
  const v = key === 'template' ? r.format : r[key];
  return v ? [v] : [];
};

/** Distinct values of one field across the rows, in first-seen order (CPT codes sorted). */
export function distinctValues(rows: ProgressQueueRow[], key: MultiKey): string[] {
  const seen: string[] = [];
  for (const r of rows) {
    for (const v of valuesOf(r, key)) if (!seen.includes(v)) seen.push(v);
  }
  return key === 'cpt' ? seen.sort() : seen;
}

const localDay = (iso: string, end: boolean): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return end
    ? new Date(+m[1]!, +m[2]! - 1, +m[3]!, 23, 59, 59, 999)
    : new Date(+m[1]!, +m[2]! - 1, +m[3]!);
};

/**
 * Rows whose value is one of the picked values for every multi filter (none picked = any),
 * and whose session falls in the period: on or after midnight N days ago, or between From
 * and To inclusive for a custom range (either end may be open).
 */
export function applyFilters(
  rows: ProgressQueueRow[],
  f: NoteFilters,
  now: Date = new Date(),
): ProgressQueueRow[] {
  let cutoff: Date | null = null;
  let from: Date | null = null;
  let to: Date | null = null;
  if (f.period === 'custom') {
    from = f.from ? localDay(f.from, false) : null;
    to = f.to ? localDay(f.to, true) : null;
  } else if (f.period) {
    const p = PERIODS.find((x) => x.id === f.period);
    if (p && p.days) {
      cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      cutoff.setDate(cutoff.getDate() - p.days);
    }
  }
  return rows.filter((r) => {
    for (const k of MULTI_KEYS) {
      const picked = f[k];
      if (picked.length && !valuesOf(r, k).some((v) => picked.includes(v))) return false;
    }
    const at = noteDate(r.date).getTime(); // ehr#484: the note's own day, not the evening before
    if (cutoff && at < cutoff.getTime()) return false;
    if (from && at < from.getTime()) return false;
    if (to && at > to.getTime()) return false;
    return true;
  });
}

export function anyFilterSet(f: NoteFilters): boolean {
  return MULTI_KEYS.some((k) => f[k].length > 0) || !!f.period;
}

/**
 * Drop any picked value the open list can't offer, so a pick made on one list never
 * silently empties the next. Status only applies to History.
 */
export function pruneFilters(
  f: NoteFilters,
  rows: ProgressQueueRow[],
  list: QueueListId,
): NoteFilters {
  const out = { ...f };
  for (const k of MULTI_KEYS) {
    const ok = k === 'status' && list !== 'history' ? [] : distinctValues(rows, k);
    out[k] = f[k].filter((v) => ok.includes(v));
  }
  return out;
}

/** 'N notes', or 'Showing N of M' when filters or the search hide some ('' when empty). */
export function resultText(shown: number, total: number): string {
  if (!total) return '';
  if (shown === total) return total === 1 ? '1 note' : `${total} notes`;
  return `Showing ${shown} of ${total}`;
}
